import type {
  EmailDeliveryCategory,
  EmailDeliveryStatus,
} from "@prisma/client";
import { Prisma } from "@prisma/client";
import { simplePricingValues } from "./pricing.types";

export const orderItemValues = Prisma.validator<Prisma.OrderItemDefaultArgs>()({
  include: {
    buyableItem: {
      include: {
        badgeType: {
          include: {
            event: {
              select: {
                id: true,
                name: true,
              },
            },
            pricing: {
              include: {
                price: true,
              },
            },
          },
        },
        ticketType: {
          include: {
            event: {
              select: {
                id: true,
                name: true,
              },
            },
            pricing: {
              include: {
                price: true,
              },
            },
            activity: {
              select: {
                structureId: true,
              },
            },
          },
        },
        advancedStatisticsEvent: true,
        exhibitorAdvancedStatisticsEvent: true,
        exhibitorProfilesPack1CreditsOrganiser: true,
        exhibitorProfilesPack2CreditsOrganiser: true,
        exhibitorProfilesPack3CreditsOrganiser: true,
        promotionEvent: true,
        exhibitorPromotionEvent: true,
        eventEquipmentRequest: {
          include: {
            event: {
              select: {
                id: true,
                name: true,
              },
            },
            eventEquipmentAllocations: {
              include: {
                eventEquipment: {
                  select: {
                    id: true,
                    name: true,
                  },
                },
              },
            },
          },
        },
        feeEventEquipmentRequest: {
          include: {
            event: {
              select: {
                id: true,
                name: true,
              },
            },
          },
        },
        exportExhibitorsModuleEvent: true,
        exportProgramModuleEvent: true,
        cashRegisterCreditsEvent: true,
        creditNote: {
          select: {
            id: true,
            reference: true,
            creditNoteItems: {
              select: {
                orderItem: {
                  select: {
                    finalTotal: true,
                    ticket: {
                      select: {
                        ticketType: {
                          select: {
                            event: {
                              select: {
                                id: true,
                                name: true,
                              },
                            },
                          },
                        },
                      },
                    },
                    badge: {
                      select: {
                        badgeType: {
                          select: {
                            event: {
                              select: {
                                id: true,
                                name: true,
                              },
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
    appliedDiscountCodes: {
      include: {
        discountCode: {
          select: {
            name: true,
            discount: {
              select: {
                type: true,
                value: true,
              },
            },
          },
        },
      },
    },
  },
} satisfies Prisma.OrderItemDefaultArgs);

export type SelectedOrderItem = Prisma.OrderItemGetPayload<
  typeof orderItemValues
>;

export const orderForSalesValues = Prisma.validator<Prisma.OrderDefaultArgs>()({
  include: {
    onlineCardPayment: { select: { id: true } },
    cashRegisterPayment: { select: { paymentMethod: true } },
    proPaymentMethod: { select: { paymentType: true } },
    onlineDetails: {
      select: {
        user: {
          select: {
            firstName: true,
            lastName: true,
            email: true,
            roles: true,
            id: true,
          },
        },
        invoice: {
          select: {
            reference: true,
          },
        },
      },
    },
    orderItems: {
      include: {
        buyableItem: {
          select: {
            ticketTypeId: true,
            badgeTypeId: true,
            badgeType: {
              select: {
                id: true,
                name: true,
                pricing: simplePricingValues,
              },
            },
            ticketType: {
              select: {
                id: true,
                name: true,
                pricing: simplePricingValues,
              },
            },
          },
        },
        badge: {
          select: {
            code: true,
            isRefunded: true,
            badgeType: {
              select: {
                id: true,
                name: true,
                pricing: simplePricingValues,
              },
            },
            ScannedTicket: {
              select: {
                id: true,
              },
            },
          },
        },
        ticket: {
          select: {
            code: true,
            isRefunded: true,
            ticketType: {
              select: {
                id: true,
                name: true,
                pricing: simplePricingValues,
              },
            },
            ScannedTicket: {
              select: {
                id: true,
              },
            },
          },
        },
      },
    },
    creditNotes: {
      take: 1,
      orderBy: {
        createdAt: "desc",
      },
      select: {
        id: true,
        status: true,
        createdAt: true,
        refundedAt: true,
        file: {
          select: {
            key: true,
            type: true,
          },
        },
        refundOrder: {
          select: {
            onlineDetails: {
              select: {
                invoice: {
                  select: {
                    file: {
                      select: {
                        key: true,
                        type: true,
                      },
                    },
                  },
                },
              },
            },
          },
        },
        creditNoteItems: {
          select: {
            orderItemId: true,
          },
        },
      },
    },
  },
});

export type SelectedOrderForSales = Prisma.OrderGetPayload<
  typeof orderForSalesValues
>;

export type TicketOrderRow = {
  rowId: string;
  rowType: "order" | "invitation";
  orderId: string | null;
  invitationId: string | null;
  orderReference: string;
  hasTickets: boolean;
  hasBadges: boolean;
  latestTicketEmailDelivery: {
    status: EmailDeliveryStatus;
    templateName: string | null;
    category: EmailDeliveryCategory;
    sentAt: string | null;
    deliveredAt: string | null;
    openedAt: string | null;
    lastEventAt: string | null;
  } | null;
  user: {
    firstName: string | null;
    lastName: string | null;
    email: string;
    connectId: string;
  };
};

export type TicketOrderFilters = {
  search?: string | null;
  pageIndex?: number;
  pageSize?: number;
};
